import { sha1Hex } from "./hash.js";
import { normalizeM3UCatalog, parseM3UPlaylist } from "./m3u.js";
import { redactCatalog } from "./redaction.js";
import { normalizeXtreamCatalog, XtreamClient, XtreamError } from "./xtream.js";
/**
 * The key a viewer's catalog is cached under. Its FIRST 12 CHARACTERS are also
 * the id namespace every catalog id is built from (see buildXtreamCatalog), so
 * this must stay byte-identical to what production has always used — changing
 * it would re-key every user's favourites and history.
 *
 * The password is part of the identity: a password change must serve the new
 * account's catalog, never one cached under the old key.
 */
export function catalogSourceKey(source) {
    if (source.kind === "m3u")
        return `m3u:${source.m3uUrl}`;
    return `xt:${source.xtreamBaseUrl}|${source.xtreamUsername}|${source.xtreamPassword}`;
}
/**
 * Whether a panel's host answers at all — asked only AFTER a sign-in has failed
 * as "unreachable", to tell a dead server from a refused login.
 *
 * Some panels drop the connection on a wrong username or password instead of
 * answering 401, and a Cloudflare-fronted one answers 520; both read to fetch as
 * a network failure. Measured 2026-09-17 from inside the app on the owner's
 * phone: the panel root answered 401 in ~0.8 s while a wrong login failed with
 * "Failed to fetch" — so "Server unreachable, check the URL" sent the viewer to
 * re-check a URL that was correct, when the fault was the credentials. Any HTTP
 * response from the root, whatever its status, means the host is up.
 */
export async function panelAnswers(baseUrl, fetchImpl = fetch, timeoutMs = 8_000) {
    let origin;
    try {
        origin = new URL(baseUrl.trim()).origin;
    }
    catch {
        return false;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        await fetchImpl(`${origin}/`, { signal: controller.signal, redirect: "manual" });
        return true;
    }
    catch {
        return false;
    }
    finally {
        clearTimeout(timer);
    }
}
/** What to tell the viewer when validating a source fails. */
export async function sourceValidationMessage(kind, baseUrl, fetchImpl) {
    if (kind === "invalid_credentials")
        return "Invalid credentials — check your username and password.";
    if (kind === "unreachable" || kind === "timeout") {
        if (baseUrl && (await panelAnswers(baseUrl, fetchImpl))) {
            return "The server is reachable but the sign-in failed — check your username and password. " +
                "Some providers drop the connection on a wrong login instead of rejecting it.";
        }
        return "Server unreachable — check the URL and try again.";
    }
    return "Connection failed. Check your settings and try again.";
}
/**
 * Connect to an Xtream source and confirm the account can be used. An inactive
 * line (expired / banned / disabled) is an account problem the viewer must fix,
 * so it is reported as invalid credentials with a message that says so, rather
 * than as a malformed server.
 *
 * The server adds its SSRF guard (assertProviderUrl) before calling this; the
 * APK needs none, because it is only ever fetching its own viewer's provider.
 */
export async function openXtreamClient(source, fetchImpl) {
    const client = new XtreamClient(source.xtreamBaseUrl, source.xtreamUsername, source.xtreamPassword, fetchImpl ? { fetchImpl } : {});
    const validation = await client.validate();
    if (!validation.ok) {
        if (validation.reason === "inactive_account") {
            throw new XtreamError("invalid_credentials", "this account reports as expired or banned");
        }
        throw new XtreamError(validation.reason === "invalid_credentials" ? "invalid_credentials" : "malformed_response");
    }
    return client;
}
/**
 * A list the provider has categories for but returned empty is a hiccup, not an
 * empty library: ask once more. Seen live on 2026-09-16: right after a deploy
 * the owner's provider answered get_vod_streams with [] (44,064 movies a
 * minute later), the server cached that, and the website showed no movie rows
 * for two hours. A provider that has no categories of a kind is left alone.
 */
async function listWithRetry(load, categoryCount, delayMs) {
    const first = await load();
    if (first.length > 0 || categoryCount === 0)
        return first;
    if (delayMs > 0)
        await new Promise((resolve) => setTimeout(resolve, delayMs));
    return load();
}
export async function buildXtreamCatalog(client, idNamespace, options = {}) {
    const delayMs = options.emptyListRetryDelayMs ?? 2000;
    const liveCategories = await client.getLiveCategories();
    const live = await listWithRetry(() => client.getLiveStreams(), liveCategories.length, delayMs);
    const vodCategories = await client.getVodCategories();
    const vod = await listWithRetry(() => client.getVodStreams(), vodCategories.length, delayMs);
    const seriesCategories = await client.getSeriesCategories();
    const series = await listWithRetry(() => client.getSeries(), seriesCategories.length, delayMs);
    const categoryNames = new Map([...liveCategories, ...vodCategories, ...seriesCategories]
        .filter((category) => category.category_id !== undefined && category.category_name)
        .map((category) => [String(category.category_id), category.category_name]));
    const catalog = normalizeXtreamCatalog(live, vod, series, idNamespace, client, categoryNames);
    const result = redactCatalog(catalog);
    return { channels: result.channels, vod: result.vod, series: result.series };
}
/**
 * The id namespace for a genuine (non-panel) M3U source.
 *
 * Not catalogSourceKey's first 12 characters, as Xtream uses: for an M3U that
 * is "m3u:https://" for EVERY playlist, so two playlists carrying the same
 * tvg-id would give their channels the same id, and a favourite or a lock on
 * one would apply to the other. Nothing was ever keyed by M3U ids — a genuine
 * M3U never got past the connection test until 2026-09-18 — so this is free to
 * be right from the start.
 */
export function m3uIdNamespace(m3uUrl) {
    return sha1Hex(catalogSourceKey({ kind: "m3u", m3uUrl: m3uUrl.trim() })).slice(0, 12);
}
/**
 * Build a viewer's catalog from a genuine M3U playlist's text — shared by the
 * server and the APK, like buildXtreamCatalog, so both give every item the
 * same id. Stream URLs are credential-redacted the same way.
 *
 * Throws malformed_response for text that is not a playlist (a provider's HTML
 * error page served with a 200, say).
 */
export function buildM3UCatalog(raw, idNamespace) {
    let catalog;
    try {
        catalog = normalizeM3UCatalog(parseM3UPlaylist(raw), idNamespace);
    }
    catch {
        throw new XtreamError("malformed_response", "malformed playlist");
    }
    const guideUrl = firstGuideUrl(catalog.epgUrl);
    const result = redactCatalog(catalog);
    return { channels: result.channels, vod: result.vod, series: result.series, ...(guideUrl ? { guideUrl } : {}) };
}
/**
 * The guide feed named in a playlist's `#EXTM3U` line, for a caller that read
 * only that line rather than the whole playlist.
 */
export function guideUrlFromM3UHeader(line) {
    const match = /\b(?:x-tvg-url|url-tvg)\s*=\s*"([^"]*)"/i.exec(line);
    return firstGuideUrl(match?.[1]);
}
/** A header may name several feeds, comma-separated; the first http(s) one is used. */
function firstGuideUrl(value) {
    return value
        ?.split(",")
        .map((part) => part.trim())
        .find((part) => /^https?:\/\//i.test(part));
}
/**
 * Count all three lists, always, sequentially. A version that stopped once a
 * threshold was met was faster but reported movies and series as 0 for a
 * source that plainly had thousands — and the real counts are information the
 * owner explicitly wants from a connection test.
 */
export async function countXtreamContent(client) {
    const channelCount = (await client.getLiveStreams()).length;
    const movieCount = (await client.getVodStreams()).length;
    const seriesCount = (await client.getSeries()).length;
    return { channelCount, movieCount, seriesCount, contentCount: channelCount + movieCount + seriesCount };
}
//# sourceMappingURL=catalog.js.map